<template lang="pug">
  .main-container.max-content(v-if='!!resource')
    .row.middle.width-100.m-bottom-4.gray.bold(v-if='redirected')
      simple-svg.gray-icon.m-right-1(:filepath='infoIcon' :width='"20px"' :height='"20px"')
      span.text-normal.bold.gray(v-if='availableLanguages.length === 1')
        | {{ $t('presentationTab.redirection') }}
        | {{ $t(`languages.${resourceAvailableLanguages[0]}`) }}
      .row.middle.text-normal.bold.gray(v-else)
        | {{ $t('presentationTab.redirection') }}
        | {{ $t(`languages.${resourceAvailableLanguages[0]}`) }}
        | {{ $t('presentationTab.and') }}
        | {{ $t(`languages.${resourceAvailableLanguages[1]}`) }}
    topics.m-bottom-3
    router-link.program.middle.m-bottom-2(v-if='resource.educationalProgram' :to='`/program/${resource.educationalProgram.id}`')
      simple-svg.m4-top-1.dark-gray-icon.m-right-2(:filepath='arrowLeftIcon' width='15px' height='15px')
      simple-svg.m4-top-1.dark-gray-icon.m-right-1(:filepath='layers' width='15px' height='15px')
      p.text-normal.dark-gray {{ resource.educationalProgram.name }}
    .row.space-between.m-bottom-5
      h1.title-big.title.item-1.blue.m4-top-3
        | {{ resource.title }}
      .row.self-start.end.m4-top-5.item-1
        button.primary-button.fern-green-button.with-icon.m-right-1(v-if='canRegisterUse' @click='showUsedDrawer')
          simple-svg.white-icon.m-right-05(:filepath='traceabilityIcon' width='18px' height='18px')
          | {{ $t("teacher.seeRegister") }}
        button.secondary-button.with-icon.m-right-1(v-if='canRegisterUse' @click='showRegisterUse')
          simple-svg.m-right-05(:filepath='checkIcon' width='18px' height='18px')
          | {{ $t("teacher.registerUse") }}
        button.secondary-button.with-icon.m-right-1(v-if='isPurchased' :title='$t("teacher.share")' type='button' @click='showShareResourceModal')
          simple-svg.blue-icon(:filepath='shareIcon' :width='"20px"' :height='"20px"')
        button.save.secondary-button.with-icon.m-right-1(v-if='isPurchased' :title='$t("teacher.addToFolder")' type='button' @click='showAddToFolderModal')
          simple-svg.white-icon(:filepath='archiveIcon' :width='"20px"' :height='"20px"')
        button.primary-button.with-icon.m-right-1(v-else type='button' @click='showModal({ element: "askForInformationModal" })')
          simple-svg.white-icon.m-right-1(:filepath='infoIcon' :width='"20px"' :height='"20px"')
          | {{ $t('teacher.getInfo') }}
        language-selector.row.middle.center.m-left-1(
          v-if='availableLanguages && availableLanguages.length > 0'
          :languages='availableLanguages'
          @change-resource-locale='locale => this.reloadResource(innerLocale)'
          :disabled='availableLanguages.length === 1'
          inner
        )
    .tab-selection.width-100.row.relative
      router-link.tab-selector.text-big.row.center(
        v-for='(tab, index) in tabs'
        :key='index'
        :to='tab.route'
        :class='{ "blocked-tab": !isPurchased && tab.shouldBlock, "active-tab": isActiveRoute(tab.routeName) }'
      )
        | {{ innerI18n.t('teacher.tab', { title: tab.name }) }}
        simple-svg.gray-icon.m-left-1(v-if='!isPurchased && tab.shouldBlock' :filepath='lockIcon' :width='"18px"' :height='"18px"')
    .tab-container(v-if='resource.presentation')
      router-view
    used-content-drawer
    add-resource-modal(v-if='showAddResourceModal' @addResource='$notify({ group: "custom-toast" })')
    share-modal(v-if='showShareModal')
    register-use-modal(v-if='showRegisterUseModal')
    feedback-modal(v-if='showFeedbackModal' @reload='reloadResource')
    done-modal(v-if='showDoneModal' :style='doneModalWidth')
    ask-for-information-modal(v-if='showAskForInformationModal')
    create-sharing-group-modal(v-if='showCreateSharingGroupModal')
    edit-sharing-group-modal(v-if='showEditSharingGroupModal')
    choice-modal(v-if='showChoiceModal')
    add-sharing-group-members-modal(v-if='showAddSharingGroupMembersModal')
    vue-headful(:title='viewTitle')
</template>

<script>
import { mapActions, mapState } from 'vuex'

import { innerI18n } from '../main'

import AddResourceModal from '@/components/Resource/AddResource'
import AskForInformationModal from '@/components/EducationalPrograms/AskForInformationModal'
import DoneModal from '@/components/Modals/DoneModal'
import FeedbackModal from '@/components/Modals/FeedbackModal'
import ShareModal from '@/components/Resource/ShareResourceModal'
import LanguageSelector from '@/components/LanguageSelector'
import CreateSharingGroupModal from '@/components/Modals/CreateSharingGroupModal'
import EditSharingGroupModal from '@/components/Modals/EditSharingGroupModal'
import AddSharingGroupMembersModal from '@/components/Modals/AddSharingGroupMembersModal'
import ChoiceModal from '@/components/Modals/ChoiceModal'
import RegisterUseModal from '@/components/RegisterUseModal/RegisterUseModal'
import UsedContentDrawer from '@/components/UsedContent/UsedContentDrawer'

import { scrollTop } from '@/utils/behavior'
import { tabTitle } from '@/utils/constants'
import { teachersTrainingReferenceId } from '@/constants/resource'

import archiveIcon from '@/assets/ic_archive.svg'
import checkIcon from '@/assets/ic_check.svg'
import traceabilityIcon from '@/assets/ic_traceability.svg'
import infoIcon from '@/assets/ic_info.svg'
import layers from '@/assets/ic_layers.svg'
import lockIcon from '@/assets/ic_lock.svg'
import shareIcon from '@/assets/ic_share.svg'
import arrowLeftIcon from '@/assets/ic_chevron-left.svg'
import { supportedLanguages } from '../constants/languages'
import { routes } from '../router'
import userRole from '../mixins/userRole'
import amplitude from 'amplitude-js'
import Topics from '../components/Resource/Topics'

export default {
  name: 'Resource',
  mixins: [userRole],
  data() {
    return {
      arrowLeftIcon,
      traceabilityIcon,
      checkIcon,
      archiveIcon,
      shareIcon,
      infoIcon,
      lockIcon,
      innerI18n,
      layers
    }
  },
  async created() {
    if (!this.customFields) await this.getUserCustomFields()
    const locale = this.$route.query.locale
    await this.hydrateResource({ id: this.$route.params.id, locale: locale || this.locale })
    this.changeInnerLocale(locale || this.resource.locale)
    amplitude.getInstance().logEvent('Resource Presentation Tab', {
      id: this.resource.id,
      name: this.resource.title,
      educationalProgram: this.resource.educationalProgram && this.resource.educationalProgram.name,
      category: this.resource.category.name
    })
  },
  computed: {
    ...mapState({
      resource: state => state.resource.resource,
      redirected: state => state.resource.redirectedResource,
      availableLanguages: state => state.resource.resource.availableLanguages,
      showAddResourceModal: state => state.behavior.addResourceModal.show,
      showAskForInformationModal: state => state.behavior.askForInformationModal.show,
      showAddSharingGroupMembersModal: state => state.behavior.addSharingGroupMembersModal.show,
      showDoneModal: state => state.behavior.doneModal.show,
      showFeedbackModal: state => state.behavior.feedbackModal.show,
      showChoiceModal: state => state.behavior.choiceModal.show,
      showShareModal: state => state.behavior.shareResourceModal.show,
      showCreateSharingGroupModal: state => state.behavior.createSharingGroupModal.show,
      showRegisterUseModal: state => state.behavior.registerUseModal.show,
      showEditSharingGroupModal: state => state.behavior.editSharingGroupModal.show,
      locale: state => state.behavior.locale,
      innerLocale: state => state.behavior.innerLocale,
      userInfo: state => state.userInfo.userInfo,
      customFields: state => state.userInfo.customFields
    }),
    canRegisterUse() {
      return !this.isTrial &&
        this.resource.isPurchased &&
        this.userInfo.schoolHasCourses &&
        !this.isTeacherTraining
    },
    isTeacherTraining() {
      return this.resource.category && this.resource.category.referenceId === teachersTrainingReferenceId
    },
    tabs() {
      const tabs = [
        {
          route: `/resource/${this.$route.params.id}`,
          name: innerI18n.t('presentationTab.presentation'),
          shouldBlock: false,
          routeName: routes.resourcePresentation
        },
        {
          route: `/resource/${this.$route.params.id}/t`,
          name: innerI18n.t('presentationTab.teacher'),
          shouldBlock: true,
          routeName: routes.teacherResource
        },
        {
          route: `/resource/${this.$route.params.id}/s`,
          name: innerI18n.t('presentationTab.student'),
          shouldBlock: true,
          routeName: routes.studentResource
        }
      ]
      return this.resource.category.referenceId === teachersTrainingReferenceId
        ? tabs.filter(tab => tab.routeName !== routes.studentResource)
        : tabs
    },
    resourceLogo() {
      return this.resource.category ? this.resource.category.imageURL : ''
    },
    tabComponent() {
      return `${this.tabs[this.activeTab].component}-tab`
    },
    doneModalWidth() {
      return '--max-width: 600px'
    },
    viewTitle() {
      return this.resource ? `${tabTitle} - ${this.resource.title}` : tabTitle
    },
    isPurchased() {
      return this.resource.isPurchased
    },
    resourceAvailableLanguages() {
      return supportedLanguages
        .filter(lang => this.availableLanguages.includes(lang.lang))
        .map(lang => lang.translation)
    },
    toastStyles() {
      return {
        width: '100%',
        display: 'flex',
        left: '0',
        'justify-content': 'center'
      }
    }
  },
  watch: {
    '$route' (to, from) {
      scrollTop()
      if (to.params.id !== from.params.id) this.hydrateResource({ id: this.$route.params.id, locale: this.innerLocale })
    }
  },
  methods: {
    ...mapActions([
      'hydrateResource',
      'showElement',
      'hideElement',
      'getShareableLink',
      'changeInnerLocale',
      'getUserCustomFields'
    ]),
    showModal(modal) {
      this.showElement(modal)
    },
    showRegisterUse() {
      this.showElement({
        element: 'registerUseModal',
        props: { resourceId: this.resource.id, programId: this.resource.educationalProgram.id }
      })
    },
    showUsedDrawer() {
      this.showElement({ element: 'usedContentDrawer', props: { resourceId: this.resource.id } })
    },
    reloadResource(locale) {
      this.hydrateResource({ id: this.$route.params.id, locale: locale || this.locale })
    },
    showAddToFolderModal() {
      this.showElement({ element: 'addResourceModal', props: { id: this.$route.params.id, resource: this.resource } })
    },
    isActiveRoute(route) {
      if (route === routes.resourcePresentation) return this.$route.name === routes.resourcePresentation
      if (route === routes.teacherResource) return this.$route.path.includes('/t')
      if (route === routes.studentResource) return this.$route.path.includes('/s')
    },
    showShareResourceModal() {
      this.showElement({
        element: 'shareResourceModal',
        props: {
          classesAmount: this.resource.teacherMaterial.classPlans.length,
          isTeacherTraining: this.isTeacherTraining
        }
      })
    }
  },
  components: {
    Topics,
    RegisterUseModal,
    UsedContentDrawer,
    ShareModal,
    FeedbackModal,
    DoneModal,
    AddResourceModal,
    AskForInformationModal,
    AddSharingGroupMembersModal,
    ChoiceModal,
    LanguageSelector,
    EditSharingGroupModal,
    CreateSharingGroupModal
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/sizes';
@import 'src/scss/variables/z-indexes';

.program{
  display: inline-flex;
}

.main-container {
  margin: 50px auto;
  min-height: calc(100vh - #{$navbar-height});
}

.title {
  max-width: 748px;
}

.save {
  --max-width: auto;
}

.resource-logo {
  margin-right: 10px;
  width: 35px;
}

.tab-selection {
  width: 450px;
  &>:not(:last-child){
    margin-right: 15px;
  }
}

.tab-selector {
  width: 140px;
  position: relative;
  padding-bottom: 15px;
  &:hover {
    color: $blue;
  }
  &::after{
    bottom: 0;
    content: "";
    border-radius: 5px;
    height: 5px;
    width: 100%;
    position: absolute;
    background-color: $light-gray;
  }
  &.active-tab {
    &::after{
      background-color: $blue;
    }
    color: $blue;
    font-weight: 700;
  }
}

.blocked-tab {
  color: $gray;
  cursor: default;
  pointer-events: none;
  position: relative;
}
</style>
